import { IEvent } from "../interfaces/models/IEvent";
import { IRegistration } from "../interfaces/models/IRegistration";

import { countSpecialEventGuestsForProgram } from "./specialEventGuests";
import { normalizeRegistrationGuests } from "./registrationGuests";

const roundInvoiceAmount = (n: number): number => Math.round(n * 100) / 100;

export interface FeeCalculationResult {
  memberFee: number;
  guestFee: number;
  guestLateFee: number;
  regularGuestTotal: number;
  lateGuestTotal: number;
  totalAmount: number;
  shouldUseLateFees: boolean;
}

export const registrationHasChargeableExtras = (registration: {
  guests?: { name?: string }[];
  guestName?: string;
  guestName2?: string;
  selectedSpecialEventIds?: string[];
  additionalFee?: number | string;
}): boolean => {
  if (normalizeRegistrationGuests(registration).length > 0) {
    return true;
  }
  if (
    Array.isArray(registration.selectedSpecialEventIds) &&
    registration.selectedSpecialEventIds.length > 0
  ) {
    return true;
  }
  const additionalFee = registration.additionalFee;
  if (typeof additionalFee === "number" && additionalFee > 0) {
    return true;
  }
  if (typeof additionalFee === "string") {
    const parsed = parseFloat(additionalFee);
    if (!isNaN(parsed) && parsed > 0) {
      return true;
    }
  }
  return false;
};

const NO_CHARGE_ATTENDEE_TYPES = new Set([
  "sponsor",
  "speaker",
  "invited_guest",
  "staff",
]);

const isNoChargeAttendeeType = (attendeeType?: string): boolean =>
  attendeeType != null && NO_CHARGE_ATTENDEE_TYPES.has(attendeeType);

const MAIN_EVENT_GUEST_FEE_WAIVED_ATTENDEE_TYPES = new Set([
  "speaker",
  "invited_guest",
]);

/** Speaker and invited guest registrants do not incur main-event guest fees (staff rule 8). */
export const shouldWaiveMainGuestFeesForRegistration = (registration: {
  attendeeType?: string;
}): boolean =>
  registration.attendeeType != null &&
  MAIN_EVENT_GUEST_FEE_WAIVED_ATTENDEE_TYPES.has(registration.attendeeType);

/** Member registrant slot is not billed when office prepaid the event, firm is host/sponsor, or attendee is no-charge. */
export const shouldWaiveRegistrantMemberFee = (registration: {
  attendeeType?: string;
  prepaid?: boolean;
  isHostFirm?: boolean;
  paymentMethod?: string;
  paymentStatus?: string;
}): boolean => {
  const paymentMethod = registration.paymentMethod;
  const paymentStatus = registration.paymentStatus;
  const isSponsorPaymentStatus =
    paymentStatus === "paidSPR" || paymentStatus === "unpaidSPR";
  const isSpeakerPaymentStatus =
    paymentStatus === "paidSPK" || paymentStatus === "unpaidSPK";
  if (registration.prepaid === true) {
    return true;
  }
  if (registration.isHostFirm === true) {
    return true;
  }
  if (isSponsorPaymentStatus) {
    return true;
  }
  if (isSpeakerPaymentStatus) {
    return true;
  }
  if (paymentMethod === "prepaid" || paymentMethod === "pendingPrepaid") {
    return true;
  }
  if (
    paymentMethod === "noCharge" &&
    isNoChargeAttendeeType(registration.attendeeType)
  ) {
    return true;
  }
  return isNoChargeAttendeeType(registration.attendeeType);
};

/** Registration timestamp for late-fee checks (matches Edit Attendee tab). */
export const resolveRegistrationDateSec = (registration: IRegistration): number => {
  if (registration.registrationDate != null) {
    return registration.registrationDate;
  }
  const attending = registration.mainEventGuestHistory?.find(
    (entry) => entry.status === "Attending",
  );
  if (attending?.registeredDate != null) {
    return attending.registeredDate;
  }
  return 0;
};

export const shouldUseLateFeesForRegistration = (
  event: IEvent | null,
  registration: IRegistration,
): boolean => {
  const fee = event?.fee;
  if (!fee) {
    return false;
  }

  const hasLateFees = (fee.guestLateFee ?? 0) > 0 || (fee.lateFee ?? 0) > 0;
  if (!hasLateFees) {
    return false;
  }

  const lateFeeDateSec = fee.lateFeeDate ?? fee.noRefundDate ?? null;
  if (lateFeeDateSec == null) {
    return false;
  }

  const regDateSec = resolveRegistrationDateSec(registration);
  return regDateSec >= lateFeeDateSec;
};

export const countRegistrationGuests = (registration: IRegistration): number =>
  normalizeRegistrationGuests(registration).length;

export const calculateEventFees = (
  event: IEvent | null,
  registration: IRegistration,
  additionalFee: number = 0
): FeeCalculationResult => {
  const fee = event?.fee;
  if (!fee) {
    return {
      memberFee: 0,
      guestFee: 0,
      guestLateFee: 0,
      regularGuestTotal: 0,
      lateGuestTotal: 0,
      totalAmount: 0,
      shouldUseLateFees: false,
    };
  }

  const numberOfGuests = shouldWaiveMainGuestFeesForRegistration(registration)
    ? 0
    : countRegistrationGuests(registration);
  const shouldUseLateFees = shouldUseLateFeesForRegistration(event, registration);

  const guestFee = fee.guestFee || 0;
  const guestLateFee = fee.guestLateFee || 0;
  const memberFee = shouldUseLateFees
    ? (fee.lateFee ?? fee.memberFee ?? fee.fee ?? 0)
    : (fee.memberFee ?? fee.fee ?? 0);

  const applicableGuestFee =
    shouldUseLateFees && guestLateFee > 0 ? guestLateFee : guestFee;
  const guestTotalAmount = applicableGuestFee * numberOfGuests;
  const regularGuestTotal =
    shouldUseLateFees && guestLateFee > 0 ? 0 : guestTotalAmount;
  const lateGuestTotal =
    shouldUseLateFees && guestLateFee > 0 ? guestTotalAmount : 0;

  let totalAmount = memberFee + guestTotalAmount + additionalFee;

  if (fee.specialEventFee !== undefined && fee.specialEventFee > 0) {
    totalAmount = fee.specialEventFee;
  }

  return {
    memberFee,
    guestFee,
    guestLateFee,
    regularGuestTotal,
    lateGuestTotal,
    totalAmount,
    shouldUseLateFees,
  };
};

/** Registration fee line on invoices (member or guest slot, after waiver rules). */
export const resolveInvoiceRegistrationFeeAmount = (
  event: IEvent | null,
  registration: IRegistration,
): number => {
  const fee = event?.fee;
  if (!fee || shouldWaiveRegistrantMemberFee(registration)) {
    return 0;
  }

  const fees = calculateEventFees(event, registration, 0);
  const isGuest = registration.attendeeType?.toLowerCase() === "guest";
  if (isGuest) {
    return fees.shouldUseLateFees
      ? (fee.guestLateFee ?? fee.guestFee ?? fee.fee ?? 0)
      : (fee.guestFee ?? fee.fee ?? 0);
  }

  return fees.memberFee;
};

/** True when the invoice should treat the member slot as firm prepaid (not billed). */
export const isInvoicePrepaidRegistration = (
  registration: IRegistration,
  options?: { hasPrepaidPaymentInLedger?: boolean },
): boolean => {
  // Sponsors / host firms are no-charge (paidSPR), never invoice "Prepaid".
  if (
    registration.attendeeType === "sponsor" ||
    registration.isHostFirm === true
  ) {
    return false;
  }
  if (registration.prepaid === true) {
    return true;
  }
  const paymentMethod = registration.paymentMethod as string | undefined;
  if (paymentMethod === "prepaid" || paymentMethod === "pendingPrepaid") {
    return true;
  }
  return options?.hasPrepaidPaymentInLedger === true;
};

/**
 * Registration fee row on invoice PDFs only.
 * Prepaid registrations always show $0 on this line (firm-covered slot).
 */
export const resolveInvoiceRegistrationFeeLineAmount = (
  event: IEvent | null,
  registration: IRegistration,
  options?: { hasPrepaidPaymentInLedger?: boolean },
): number => {
  if (isInvoicePrepaidRegistration(registration, options)) {
    return 0;
  }
  return resolveInvoiceRegistrationFeeAmount(event, registration);
};

const sumPrepaidPayments = (
  payments?: Array<{ paymentMethod?: string; amount?: number }> | null,
): number => {
  if (!payments?.length) {
    return 0;
  }
  return roundInvoiceAmount(
    payments
      .filter((p) => p.paymentMethod === "prepaid")
      .reduce(
        (sum, p) =>
          sum + (typeof p.amount === "number" && !Number.isNaN(p.amount) ? p.amount : 0),
        0,
      ),
  );
};

/**
 * Cash paid shown on invoice PDFs. Prepaid ledger rows are firm-covered, not billed cash.
 */
export const resolveInvoiceAmountPaid = (
  registration: IRegistration,
  options?: {
    hasPrepaidPaymentInLedger?: boolean;
    payments?: Array<{ paymentMethod?: string; amount?: number }> | null;
  },
): number => {
  const rawPaid = roundInvoiceAmount(
    typeof registration.amountPaid === "number" &&
      !Number.isNaN(registration.amountPaid)
      ? registration.amountPaid
      : 0,
  );

  if (!isInvoicePrepaidRegistration(registration, options)) {
    return rawPaid;
  }

  const prepaidPaid = sumPrepaidPayments(options?.payments);
  if (prepaidPaid > 0) {
    return roundInvoiceAmount(Math.max(0, rawPaid - prepaidPaid));
  }

  return 0;
};

/** Invoice total due (member slot uses prepaid line rules). */
export const calculateInvoiceTotalAmount = (
  event: IEvent | null,
  registration: IRegistration,
  additionalFee: number = 0,
  options?: { hasPrepaidPaymentInLedger?: boolean },
): number => {
  const fee = event?.fee;
  if (!fee) {
    return getSpecialEventsTotalAmount(event, registration);
  }

  const fees = calculateEventFees(event, registration, additionalFee);
  const regAmount = resolveInvoiceRegistrationFeeLineAmount(
    event,
    registration,
    options,
  );
  const guestTotal = fees.regularGuestTotal + fees.lateGuestTotal;
  const specialEventsTotal = getSpecialEventsTotalAmount(event, registration);

  return regAmount + guestTotal + additionalFee + specialEventsTotal;
};

export const calculateInvoiceAmount = (
  event: IEvent | null,
  registration: IRegistration,
  additionalFee: number = 0
): number => {
  const fee = event?.fee;
  if (!fee) {
    return getSpecialEventsTotalAmount(event, registration);
  }

  const fees = calculateEventFees(event, registration, additionalFee);
  const regAmount = resolveInvoiceRegistrationFeeAmount(event, registration);

  const guestTotal = fees.regularGuestTotal + fees.lateGuestTotal;
  const specialEventsTotal = getSpecialEventsTotalAmount(event, registration);

  return regAmount + guestTotal + additionalFee + specialEventsTotal;
};

/**
 * Staff balance: chargeable total minus amount paid.
 * Prepaid member-slot rows are excluded from the paid amount.
 * The result can be negative when amount paid exceeds the charges.
 */
export const calculateInvoiceAmountDue = (
  event: IEvent | null,
  registration: IRegistration,
  additionalFee: number = 0,
  options?: {
    hasPrepaidPaymentInLedger?: boolean;
    payments?: Array<{ paymentMethod?: string; amount?: number }> | null;
  },
): number => {
  const total = calculateInvoiceTotalAmount(
    event,
    registration,
    additionalFee,
    options,
  );
  const rawPaid = roundInvoiceAmount(
    typeof registration.amountPaid === "number" &&
      !Number.isNaN(registration.amountPaid)
      ? registration.amountPaid
      : 0,
  );
  const prepaidPaid = sumPrepaidPayments(options?.payments);
  const memberFee = roundInvoiceAmount(
    event?.fee?.memberFee ?? event?.fee?.fee ?? 0,
  );
  let appliedPaid = rawPaid;
  if (isInvoicePrepaidRegistration(registration, options) && prepaidPaid > 0) {
    appliedPaid = roundInvoiceAmount(Math.max(0, rawPaid - prepaidPaid));
  } else if (
    isInvoicePrepaidRegistration(registration, options) &&
    memberFee > 0 &&
    rawPaid >= memberFee
  ) {
    appliedPaid = roundInvoiceAmount(rawPaid - memberFee);
  }
  return roundInvoiceAmount(total - appliedPaid);
};

/**
 * Prepaid ledger rows can record the waived member slot in amountPaid.
 * Speaker / sponsor / invited-guest cash payments never include that slot, even
 * when the amount happens to equal the member fee.
 */
export const amountPaidMayIncludeAutoSettledMemberSlot = (registration: {
  prepaid?: boolean;
  paymentMethod?: string;
}): boolean => {
  if (registration.prepaid === true) {
    return true;
  }
  const method = registration.paymentMethod;
  return method === "prepaid" || method === "pendingPrepaid";
};

/** Prepaid auto-settlement on the member slot must not reduce guest balances. */
export const getAmountPaidTowardChargeableExtras = (
  event: IEvent | null,
  registration: IRegistration,
  additionalFee: number = 0,
): number => {
  const amountPaid = roundInvoiceAmount(
    typeof registration.amountPaid === "number" &&
      !Number.isNaN(registration.amountPaid)
      ? registration.amountPaid
      : 0,
  );
  if (!shouldWaiveRegistrantMemberFee(registration)) {
    return amountPaid;
  }
  if (!registrationHasChargeableExtras(registration)) {
    return 0;
  }
  if (!amountPaidMayIncludeAutoSettledMemberSlot(registration)) {
    return amountPaid;
  }
  if (!event) {
    return amountPaid;
  }
  const payable = roundInvoiceAmount(
    calculateInvoiceAmount(event, registration, additionalFee),
  );
  const fullTotal = roundInvoiceAmount(
    calculateEventFees(event, registration, additionalFee).totalAmount,
  );
  const memberPortion = roundInvoiceAmount(fullTotal - payable);
  if (memberPortion > 0 && amountPaid === memberPortion) {
    return 0;
  }
  return amountPaid;
};

export const determinePaymentStatus = (
  totalAmount: number,
  paidAmount: number
): "paid" | "partial" | "unpaid" => {
  if (totalAmount <= 0) {
    return "paid";
  } else if (paidAmount >= totalAmount) {
    return "paid";
  } else if (paidAmount > 0) {
    return "partial";
  }
  return "unpaid";
};

export const generateInvoiceFilename = (
  event: IEvent | null,
  personName: string,
  paymentStatus: "paid" | "partial" | "unpaid"
): string => {
  const eventName = (event?.title || "Event")
    .replace(/[^a-zA-Z0-9\s-]/g, "")
    .replace(/\s+/g, "_");
  const memberName = (personName || "Member")
    .replace(/[^a-zA-Z0-9\s-]/g, "")
    .replace(/\s+/g, "_");
  const invoiceType = paymentStatus === "paid" ? "Paid_Invoice" : paymentStatus === "partial" ? "Partial_Invoice" : "Due_Invoice";
  const currentTimestamp = Math.floor(Date.now() / 1000);
  const date = new Date(currentTimestamp * 1000);
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = date.getUTCDate().toString().padStart(2, "0");
  const year = date.getUTCFullYear();
  const dateStr = `${month}-${day}-${year}`;
  return `${invoiceType}_${eventName}_${memberName}_${dateStr}.pdf`;
};

export const getInvoiceTypeForEmail = (
  paymentStatus: "paid" | "partial" | "unpaid"
): "Paid Invoice" | "Due Invoice" => {
  return paymentStatus === "paid" ? "Paid Invoice" : "Due Invoice";
};

export const getPaymentStatusForHistory = (
  paymentStatus: "paid" | "partial" | "unpaid"
): "completed" | "partial" | "pending" => {
  return paymentStatus === "paid" ? "completed" : paymentStatus === "partial" ? "partial" : "pending";
};

export const parseAdditionalFee = (additionalFee: any): number => {
  if (typeof additionalFee === "number") {
    return additionalFee;
  } else if (typeof additionalFee === "string") {
    const parsed = parseFloat(additionalFee);
    return !isNaN(parsed) ? parsed : 0;
  }
  return 0;
};

/**
 * Total amount for selected special events (attendee fees + guest fees per program).
 */
export const getSpecialEventsTotalAmount = (
  event: IEvent | null,
  registration: {
    selectedSpecialEventIds?: string[];
    specialEventGuests?: Record<string, { name?: string }[]>;
    specialEventGuestNames?: Record<string, string | string[]>;
  }
): number => {
  const program = event?.program;
  if (!program?.length) {
    return 0;
  }

  const byIds = registration?.selectedSpecialEventIds;
  if (!Array.isArray(byIds) || byIds.length === 0) {
    return 0;
  }

  const byIdMap = new Map(program.filter((p: any) => p.id).map((p: any) => [p.id, p]));
  let total = 0;
  for (const id of byIds) {
    const prog = byIdMap.get(id);
    if (!prog) {
      continue;
    }
    total += prog.fee ?? 0;
    const guestCount = countSpecialEventGuestsForProgram(registration, id);
    total += (prog.specialEventGuestFee ?? 0) * guestCount;
  }
  return total;
};
